import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { ArrowLeft, ChevronDown, Search } from 'lucide-react';

// 20 platform-specific FAQs — grouped loosely but rendered as a single accordion.
const FAQS = [
  {
    q: 'What is KrazyBets?',
    a: 'KrazyBets is a peer-to-peer (P2P) betting platform where users wager against each other — not the house. We facilitate the match, hold stakes in escrow, verify outcomes and release the winnings. You can bet on sports, entertainment, weather, politics, news and more, in 1–48 hour windows.',
  },
  {
    q: 'Who can sign up?',
    a: 'Anyone 18 years or older and residing in a jurisdiction where online betting is legal. We enforce strict KYC (Know Your Customer) verification before any real-money activity.',
  },
  {
    q: 'How do I create an account?',
    a: 'Tap Sign Up, enter your name, email, country, mobile number and a strong password, complete the CAPTCHA, verify your email and you are in. You instantly receive 100 free Qbean tokens to play with.',
  },
  {
    q: 'What are Qbean tokens (QBC)?',
    a: 'Qbean (QBC) is our in-platform virtual currency used to create and match bets, play Rock-Paper-Scissors duels, and receive promotions. QBC only has value inside KrazyBets; real-money conversion requires approved payment/withdrawal methods and KYC.',
  },
  {
    q: 'How do I create a bet?',
    a: 'From the Dashboard, tap Create Bet, pick a category, write a clear title and a YES/NO resolvable outcome, set your stake, pick a deadline (1h–48h) and choose Public or Private visibility. Our AI engine and moderators review it — usually within minutes — before it goes live.',
  },
  {
    q: 'What is an Initiator vs a Challenger?',
    a: 'The Initiator is the user who creates the bet and locks the first stake. The Challenger is the user who accepts (matches) the bet with an equal stake on the opposite outcome. Both stakes are held in escrow until the bet resolves.',
  },
  {
    q: 'How does the escrow system work?',
    a: 'When a bet is matched, both stakes are locked in platform escrow (your "Escrow Locked" wallet balance). When the outcome is verified, the winner receives both stakes minus our commission. Funds are never touched manually by admins except on admin-approved disputes.',
  },
  {
    q: 'What bets are NOT allowed?',
    a: 'Bets involving minors, violence, adult/pornographic content, illegal activities, or anything that cannot be resolved from a public, verifiable source. Our AI engine auto-flags these and moderators will decline with a mandatory reason.',
  },
  {
    q: 'What commission does KrazyBets charge?',
    a: 'Commission is based on your subscription tier: Free = 10% of winnings, Green ($5/mo) = 5%, Gold ($10/mo) = 3%. Unmatched expired bets are refunded minus a 10% unmatched fee (Free tier).',
  },
  {
    q: 'How are Rock-Paper-Scissors (RPS) duels played?',
    a: 'RPS uses a cryptographic commit-reveal system. Each player commits a hashed move, both reveal simultaneously, and the winner is decided best-of-three with a 60-second timer per round. Optional live 3-panel webcam (WebRTC) is available for high-stakes or moderated duels.',
  },
  {
    q: 'Why does my RPS camera not show up?',
    a: 'Make sure your browser has granted camera and microphone permission. Both players must enable their camera for the 3-panel view. On iOS, use Safari for WebRTC support.',
  },
  {
    q: 'How do I deposit or withdraw real money?',
    a: 'Go to Wallet → Deposit / Withdraw. We support Stripe, PayPal and crypto (Bitcoin, USDT). All withdrawals require approved KYC and may take 1–3 business days depending on method. Currently some payment rails are in sandbox mode — check the banner at the top of the Wallet page.',
  },
  {
    q: 'What happens if my bet is not matched before the deadline?',
    a: 'Your bet is marked Expired. You get a refund minus the unmatched fee based on your tier (10% Free, 3% Green, 1% Gold). Refunds are instant to your Qbean or cash balance.',
  },
  {
    q: 'What is KYC and do I have to complete it?',
    a: 'KYC (Know Your Customer) is regulatory identity verification. You must submit a government-issued ID, a selfie and proof of address before any real-money withdrawal or when your balance exceeds platform limits. Review normally completes in 24–48 hours.',
  },
  {
    q: 'How does the AI chatbot help me?',
    a: 'The gold chat bubble at the bottom-right of every page connects you to our AI assistant, trained on all KrazyBets rules. It can explain bets, walk you through KYC, resolve deadline issues and create a human support ticket if needed.',
  },
  {
    q: 'Can I dispute a bet result?',
    a: 'Yes. Open Tickets, select the bet, provide evidence (source link, screenshot) and submit. A moderator will review within 48 hours. If your evidence is valid, the bet is re-resolved and escrow is released accordingly.',
  },
  {
    q: 'How do subscriptions (Green / Gold) work?',
    a: 'Green ($5/month) lowers commission to 5%, lifts your daily bet cap to 10, and gives a green badge. Gold ($10/month) reduces commission to 3%, grants unlimited daily bets, moderator eligibility, priority support and a gold badge. Cancel anytime in Settings → Subscription.',
  },
  {
    q: 'Is my money and data safe?',
    a: 'Yes. All traffic is encrypted via TLS/SSL, passwords are hashed with bcrypt, payments are handled by PCI-compliant processors, and personal data follows GDPR and POPIA principles. Access to your data is restricted to authorised staff and audit-logged.',
  },
  {
    q: 'How do I close or self-exclude my account?',
    a: 'Visit the Responsible Gambling page or email info@krazybets.online with the duration you need (24h, 7 days, 30 days or permanent). Your account will be frozen within 24 hours and any remaining balance will be processed for withdrawal subject to KYC.',
  },
  {
    q: 'How do I contact a human if the chatbot cannot help?',
    a: 'Head to the Contact Us page and send us a message — every submission lands directly with our support team and we aim to reply within one hour, 24/7. You can also open a support ticket via Tickets inside your account.',
  },
];

const AccordionItem = ({ item, idx, open, onToggle }) => (
  <div className={`border border-[#3D3D3D] rounded-lg overflow-hidden transition-all ${open ? 'bg-[#232323]' : 'bg-[#1F1F1F]'}`} data-testid={`faq-item-${idx}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left"
      data-testid={`faq-toggle-${idx}`}
    >
      <span className="flex items-start gap-3 min-w-0">
        <span className="text-[#D4AF37] font-black text-sm mt-0.5 flex-shrink-0">Q{idx + 1}</span>
        <span className="text-white font-semibold">{item.q}</span>
      </span>
      <ChevronDown className={`h-5 w-5 text-[#D4AF37] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && (
      <div className="px-4 pb-4 text-sm text-gray-300 leading-relaxed border-t border-[#3D3D3D] pt-3">
        {item.a}
      </div>
    )}
  </div>
);

const FAQPage = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const [search, setSearch] = useState('');
  const filtered = FAQS.filter((f) =>
    f.q.toLowerCase().includes(search.toLowerCase()) ||
    f.a.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full" data-testid="faq-page">
        <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
        <h1 className="text-3xl font-black uppercase text-white mb-2" style={{ fontFamily: 'Outfit' }}>
          Frequently Asked <span className="text-[#D4AF37]">Questions</span>
        </h1>
        <p className="text-gray-400 mb-6 text-sm">20 of the most common questions from our community. Tap a question to expand.</p>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the FAQ..."
            className="input-dark w-full pl-10"
            data-testid="faq-search"
          />
        </div>

        <div className="space-y-3">
          {filtered.map((item, idx) => {
            const realIdx = FAQS.indexOf(item);
            return (
              <AccordionItem
                key={realIdx}
                item={item}
                idx={realIdx}
                open={openIdx === realIdx}
                onToggle={() => setOpenIdx(openIdx === realIdx ? -1 : realIdx)}
              />
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-10 text-gray-500">No matches. <Link to="/contact" className="text-[#D4AF37] hover:underline">Ask us directly.</Link></div>
          )}
        </div>

        <div className="mt-10 card-dark text-center">
          <p className="text-gray-300 text-sm mb-3">Still have questions?</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact" className="btn-primary">Contact Support</Link>
            <Link to="/help" className="tab-3d text-white">Browse Help Centre</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FAQPage;
